'use client';
import { useTranslations } from 'next-intl';
import NiceModal from '@ebay/nice-modal-react';
import { useAtomValue } from 'jotai';
import useFn from '@/hooks/use-fn';
import { EmblaCarousel } from '../components/embla-carousel';
import { CardNormalItem, RummyIconType } from '../services/home';
import { selectedCurrencyAtom, userAtom } from '@/store';
import { Link } from '@/i18n/navigation';
import RournamentImg from '@/features/home/images/card-rummy-tournament.png';
import PointsImg from '@/features/home/images/card-rummy-points.png';
import DealsImg from '@/features/home/images/card-rummy-deals.png';
import FantasyImg from '@/features/home/images/card-fantasy2.jpeg';
import SportsImg from '@/features/home/images/card-sports.jpg';
import ModalWarn from '../components/modal-warn';
import { GameStatus } from '@/features/casino/services/home';
import ImageLoad from '../components/image-load';
import { AudioId, useAudioHelper } from '@/utils/audio-hepler';
import { menuTabUrl } from '@/const/page-url';
import Maintenance from '../components/maintenance';
import { GAME_TYPE } from '../const/game';
import GameCardWithTracking from '../components/game-card-with-tracking';
import { useCallback } from 'react';

const CURRENT_PAGE = 'Home';
const CURRENT_MODULE = 'recently played';

interface RecentlyPlayedCardProps {
  item: CardNormalItem;
  index: number;
  link: string;
  renderImage: (item: CardNormalItem) => any;
  handleUnavailable: (imgUrl: string) => void;
  rummyImgMap: Record<RummyIconType, any>;
  playAudio: () => void;
}

function RecentlyPlayedCard({
  item,
  index,
  link,
  renderImage,
  handleUnavailable,
  rummyImgMap,
  playAudio,
}: RecentlyPlayedCardProps) {

  const renderGameCard = useCallback(() => {
    return (
      <GameCardWithTracking
        item={item}
        index={index}
        currentPage={CURRENT_PAGE}
        currentModule={CURRENT_MODULE}
        throttleWait={40 * 1000}
      >
        {({ cardRef, handleClick }) => {
          const wrappedNavigate = async (e: any) => {
            playAudio();
            if (item.status !== GameStatus.NORMAL) {
              e.preventDefault();
              if (Boolean(item.game_id)) {
                handleUnavailable(rummyImgMap[item.rummy_icon_type as RummyIconType].src);
              } else {
                handleUnavailable(item.icon || '');
              }
              return;
            }
            // 只有在游戏状态正常时才发送点击埋点
            await handleClick();
          };

          return (
            <div
              ref={cardRef}
              className={`aspect-[130/171] active:scale-90 relative rounded-[10px] overflow-hidden cursor-pointer group transition-all duration-300 ease-out hover:shadow-2xl transform-gpu will-change-transform`}
            >
              <Link href={link} onNavigate={wrappedNavigate}>
                {Boolean(item?.game_type) ? (
                  <ImageLoad
                    skeletonWidth={52}
                    skeletonHeight={29}
                    src={renderImage(item)}
                    className='object-cover'
                    alt={'card'}
                    fill
                    priority={index < 3}
                  />

                ) : (
                  <div className='w-full h-full' style={{
                    background: 'linear-gradient(150.15deg, #2283f625 27.17%, #2283f615 46.76%, #2283f600 61.36%), #2a3546',
                  }} />
                )}
                {item.status !== GameStatus.NORMAL && <Maintenance />}
              </Link>
            </div>
          );
        }}
      </GameCardWithTracking>
    );
  }, [item, index, link, renderImage, handleUnavailable, rummyImgMap, playAudio]);
  return (
    <>
      {renderGameCard()}
    </>
  );
}


interface RecentlyPlayedProps {
  recentlyPlayedGames: CardNormalItem[];
  title: string;
}

export default function RecentlyPlayed(props: RecentlyPlayedProps) {
  const { recentlyPlayedGames = [], title = '' } = props;
  const t = useTranslations('home');
  const user = useAtomValue(userAtom);
  const userGlobalBalanceCode = useAtomValue(selectedCurrencyAtom);
  const { playAudio } = useAudioHelper({ id: AudioId.TurnPage });

  const rummyImgMap = {
    [RummyIconType.Rournament]: RournamentImg,
    [RummyIconType.Points]: PointsImg,
    [RummyIconType.Deals]: DealsImg,
  };

  const renderImage = (item: CardNormalItem) => {
    if (item.game_type === GAME_TYPE.Fantasy) {
      return FantasyImg;
    };
    if (item.game_type === GAME_TYPE.Sports) {
      return SportsImg;
    };
    if (item.game_type === GAME_TYPE.Rummy) {
      return rummyImgMap[item.rummy_icon_type as RummyIconType];
    } else {
      return item.icon || '';
    }
  };

  const handleUnavailable = useFn((imgUrl: string) => {
    NiceModal.show(ModalWarn, {
      title: t('gameError'),
      imgUrl: imgUrl,
      imgWidth: 130,
      imgHeight: 171,
    });
  });

  return (
    <EmblaCarousel
      title={title}
      colors={['#31F34A', '#049810']}
      showViewAll={false}
      className="pl-[12px]"
    // onClickViewAll={handleViewAll}
    >
      {
        recentlyPlayedGames.map((item, index) => {
          let link = '';
          if (item.game_type === GAME_TYPE.Rummy) {
            link = `${menuTabUrl.rummy}`;
          } else if (item.game_type === GAME_TYPE.Fantasy) {
            link = `${menuTabUrl.fantasy}`;
          } else if (item.game_type === GAME_TYPE.Sports) {
            link = `${menuTabUrl.sports}`;
          } else {
            // casino
            if (user.accessToken && item.currency_type?.includes(userGlobalBalanceCode || '')) {
              link = `/casino/games/play?id=${item?.id}_${item.name}&currency=${userGlobalBalanceCode}`;
            } else {
              link = `/casino/games/detail?id=${item?.id}_${item.name}`;
            }
          }
          return (
            <RecentlyPlayedCard
              key={item?.id || index}
              item={item}
              index={index}
              link={link}
              renderImage={renderImage}
              handleUnavailable={handleUnavailable}
              rummyImgMap={rummyImgMap}
              playAudio={playAudio}
            />
          );
        })}
    </EmblaCarousel >
  );
};